import { create } from 'zustand';
import { ApiError, toApiError } from '@app/utils/ApiError';
import { ENTITY_STORE_PAGE_SIZE } from '@app/utils/config';

export interface EntityApi<T> {
  getAll: (params?: Record<string, string | number | boolean | null | undefined>) => Promise<T[]>;
  getById: (id: string | number) => Promise<T>;
  create: (data: Partial<T>) => Promise<T>;
  update: (id: string | number, data: Partial<T>) => Promise<T>;
  remove: (id: string | number) => Promise<void>;
}

export interface EntityStore<T> {
  items: T[];
  isLoaded: boolean;
  /**
   * Why the last loadAll() failed, or null. Parked here instead of being thrown
   * because loadAll() is called fire-and-forget by views; see loadAll below.
   */
  error: ApiError | null;
  setItems: (items: T[]) => void;
  upsertItem: (updatedItem: T) => void;
  removeItem: (itemId: string | number) => void;

  /**
   * Applies this store's own ingestor to a raw server payload, then upserts it.
   * Lets external pushes (WebSocket sync) land correctly-shaped entities without
   * the caller needing to know which ingestor belongs to which entity.
   */
  syncItem: (raw: T) => void;

  /**
   * Loads the collection and records any failure on `error` instead of rejecting.
   * Safe to call fire-and-forget, which is how views use it.
   */
  loadAll: (forceRefresh?: boolean, params?: Record<string, string | number | boolean | null | undefined>) => Promise<void>;

  /**
   * Same load, but rejects on failure. Used when one store is another's dependency:
   * the dependent store must not cache half-hydrated entities built from a dictionary
   * that never arrived.
   */
  loadAllOrThrow: (forceRefresh?: boolean, params?: Record<string, string | number | boolean | null | undefined>) => Promise<void>;

  loadById: (id: string | number, bypassCache? : boolean) => Promise<T>;
  save: (data: Partial<T>, isNew: boolean) => Promise<T>;
  delete: (id: string | number) => Promise<void>;
  clearCache: () => void;

  listenerCount: number;
  subscribeView: () => () => void;
}

export const createEntityStore = <T extends { id: string | number }>(
  api: EntityApi<T>,
  ingestor: (data: T[]) => T[] = (data) => data,
  loadDependencies?: () => Promise<void>,
  ttlMinutes: number = 15, // Default memory lifespan
  // One request holding the whole collection, which is what lets every view filter in the browser.
  // See the note on request sizes in utils/config.ts for the headroom this assumes, and for what
  // to do when a collection starts approaching it.
  defaultParams: Record<string, string | number | boolean | null | undefined> = { size: ENTITY_STORE_PAGE_SIZE }
) => {
  // CLOSURE: Invisible to React, holds the garbage collection timer
  let memoryWipeTimer: number | null = null;
  
  // CLOSURE: Tracks in-flight network requests to prevent race conditions
  const activeGetAllPromises: Record<string, Promise<void>> = {};
  const activeGetByIdPromises: Record<string, Promise<T>> = {};

  return create<EntityStore<T>>((set, get) => {
    
    const resetGarbageCollector = () => {
      if (memoryWipeTimer) window.clearTimeout(memoryWipeTimer);
      
      memoryWipeTimer = window.setTimeout(() => {
        // DO NOT WIPE if components are actively viewing this data!
        if (get().listenerCount > 0) {
          console.debug(`[Cache] Skipped memory wipe for active view.`);
          resetGarbageCollector(); // Push the timer forward
          return;
        }

        set({ items: [], isLoaded: false });
        console.debug(`[Cache] Cleared unused memory after ${ttlMinutes}m of inactivity.`);
      }, ttlMinutes * 60 * 1000);
    };

    return {
      items: [],
      isLoaded: false,
      error: null,
      listenerCount: 0,

      // Increments when a view mounts, decrements when it unmounts
      subscribeView: () => {
        set((state) => ({ listenerCount: state.listenerCount + 1 }));
        
        return () => {
          set((state) => {
            const nextCount = Math.max(0, state.listenerCount - 1);
            if (nextCount === 0) resetGarbageCollector(); // Start GC timer on last unmount
            return { listenerCount: nextCount };
          });
        };
      },

      setItems: (data) => {
        set({ items: data, isLoaded: true, error: null });
        resetGarbageCollector();
      },

      upsertItem: (updatedItem) => set((state) => {
        const exists = state.items.some(item => item.id === updatedItem.id);
        if (exists) return { items: state.items.map(item => item.id === updatedItem.id ? updatedItem : item) };
        return { items: [...state.items, updatedItem] };
      }),

      removeItem: (itemId) => set((state) => ({
        items: state.items.filter(item => item.id !== itemId)
      })),

      syncItem: (raw) => {
        // Cold cache: there is nothing to keep in sync. Ingesting now would only
        // resolve against unloaded dictionaries and yield a half-hydrated entity;
        // the next loadAll() fetches it properly anyway.
        if (!get().isLoaded) return;

        const ingested = ingestor([raw])[0];
        if (ingested) get().upsertItem(ingested);
      },

      clearCache: () => {
        if (memoryWipeTimer) window.clearTimeout(memoryWipeTimer);
        set({ items: [], isLoaded: false, error: null });
      },

      loadAllOrThrow: async (forceRefresh = false, params) => {
        // 1. Check cache first
        if (get().isLoaded && !forceRefresh) {
          resetGarbageCollector();
          return;
        }

        const finalParams = { ...defaultParams, ...params };

        // 2. Create a unique string key based on the parameters
        // If no params are passed, it defaults to the string "{}"
        const paramKey = JSON.stringify(params || {});

        // 3. If THIS EXACT query is already in-flight, await that instead
        if (activeGetAllPromises[paramKey]) {
          return activeGetAllPromises[paramKey];
        }

        // Clear any previous failure so a retry renders as "loading", not "still broken"
        if (get().error) set({ error: null });

        // 4. Create a new Promise block and lock it into the closure dictionary
        activeGetAllPromises[paramKey] = (async () => {
          try {
            if (loadDependencies) await loadDependencies();

            // 5. Pass the params down to your dumb API layer!
            const rawData = await api.getAll(finalParams);
            const ingestedData = ingestor(rawData);

            set({ items: ingestedData, isLoaded: true, error: null });
            resetGarbageCollector();
          } finally {
            // Ensure the lock is released whether the fetch succeeds or fails
            delete activeGetAllPromises[paramKey];
          }
        })();

        return activeGetAllPromises[paramKey];
      },

      loadAll: async (forceRefresh = false, params) => {
        try {
          await get().loadAllOrThrow(forceRefresh, params);
        } catch (e) {
          // Deliberately not rethrown. Views call loadAll() fire-and-forget, so a
          // rejection here surfaces as an unhandled rejection — a dev-server overlay
          // in development, a silently empty page in production. Parking it on the
          // store lets the view render a reason and offer a retry instead.
          // save/delete/loadById still reject; those have a caller ready to catch.
          const error = toApiError(e);
          console.error('[Store] loadAll failed:', error.message, error);
          set({ error, isLoaded: false });
        }
      },

      loadById: async (id: string | number, bypassCache: boolean = false): Promise<T> => {
        // 1. Check cache first
        if (!bypassCache) {
          const cachedItem = get().items.find((item: T) => String(item.id) === String(id));
          if (cachedItem) return cachedItem;
        }

        const stringId = String(id);

        // 2. If this specific ID is already being fetched, wait for that Promise
        if (activeGetByIdPromises[stringId] && !bypassCache) {
          return activeGetByIdPromises[stringId];
        }

        // 3. Create a new Promise block and lock it into the closure dictionary
        activeGetByIdPromises[stringId] = (async () => {
          try {
            const fetchedData = await api.getById(id);
            const ingested: T = ingestor([fetchedData])[0]!;
            
            get().upsertItem(ingested);
            return ingested;
          } finally {
            // Ensure the lock is released whether the fetch succeeds or fails
            delete activeGetByIdPromises[stringId];
          }
        })();
        
        return activeGetByIdPromises[stringId];
      },

      save: async (data: Partial<T>, isNew: boolean) => {
        const response = isNew
          ? await api.create(data)
          : await api.update(data.id!, data);

        /*
         * POST answers 201 with the stored record, but PUT answers 204 with no body at all -
         * which fetchData hands back as `{}`. Ingesting that would cache an entity with no id
         * and hand the caller a blank record, which is what emptied a detail page the moment
         * its second save landed.
         *
         * So the record is read back instead. Reading rather than reusing what was sent is
         * deliberate: the write bumps entityVersion, and holding the stale one fails the next
         * save's optimistic-lock check.
         */
        let savedData = response;
        if ((response as Partial<T> | undefined)?.id === undefined) {
          if (data.id === undefined) {
            throw new ApiError('The service saved the record but returned nothing to identify it by.', {
              kind: 'server'
            });
          }
          savedData = await api.getById(data.id);
        }

        const ingested = ingestor([savedData])[0]!;
        get().upsertItem(ingested);
        resetGarbageCollector();
        return ingested;
      },

      delete: async (id: string | number) => {
        await api.remove(id);
        get().removeItem(id);
      }
    };
  });
};